import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronRight, Compass, ShieldCheck, HeartPulse, Shuffle,
  Target, Flame, Crown, Goal, Scale,
  Shirt, Palette, User, Briefcase,
  Phone, ClipboardList, Sparkles, PenTool, Flower2, Brain,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import DiscoveryCallDialog from "@/components/DiscoveryCallDialog";
import SEOHead from "@/components/SEOHead";
import AuthorByline from "@/components/AuthorByline";
import RelatedServices from "@/components/RelatedServices";
import { useSitePage } from "@/lib/cms";
import { getRecordArray, getString, getStringArray } from "@/lib/pageContent";
import { buildBreadcrumb, buildService, buildFAQ, CORE_FAQS } from "@/lib/seo/schemas";

const problemIcons = [Compass, ShieldCheck, HeartPulse, Shuffle];
const coachingIcons = [Target, Flame, Crown, Goal, Scale];
const brandingIcons = [Shirt, Palette, User, Briefcase];
const journeyIcons = [Phone, ClipboardList, Sparkles];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay },
});

const ServicesIndividuals = () => {
  const { data: page } = useSitePage("services-individuals");
  const content = (page?.content ?? {}) as Record<string, unknown>;

  const problems = getRecordArray<Record<string, unknown>>(content.problems, []).map((item, index) => ({
    title: getString(item.title, `Problem ${index + 1}`),
    desc: getString(item.desc),
  }));
  const coachingFocus = getStringArray(content.coachingFocus, []);
  const brandingIncludes = getStringArray(content.brandingIncludes, []);
  const journey = getRecordArray<Record<string, unknown>>(content.journey, []).map((item, index) => ({
    title: getString(item.title, `Step ${index + 1}`),
    desc: getString(item.desc),
  }));

  return (
    <div>
      <SEOHead
        title={page?.seo.title ?? "Executive Coaching Dubai — ICF Certified Coach | MVIBE"}
        description={page?.seo.description ?? "1:1 executive coaching in Dubai with an ICF-certified coach. Career clarity, leadership presence and confidence for professionals, founders and women leaders."}
        canonical={page?.seo.canonical}
        ogTitle={page?.seo.ogTitle}
        ogDescription={page?.seo.ogDescription}
        ogImage={page?.seo.ogImage}
        type={page?.seo.type ?? "website"}
        schema={page?.seo.schema ?? [
          buildService({
            name: "1:1 Life & Career Coaching for Professionals",
            description: "Personal coaching for working professionals - career clarity, burnout recovery, confidence, personal branding and image consulting with MVIBE founder Mahirah.",
            path: "/services/individuals",
            serviceType: "Life coaching",
          }),
          buildBreadcrumb([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            { name: "For Individuals", path: "/services/individuals" },
          ]),
          buildFAQ(CORE_FAQS.slice(0, 6)),
        ]}
      />

      <section className="bg-primary py-14 md:py-20">
        <div className="container mx-auto px-4 text-center">
          <nav className="mb-4 flex items-center justify-center gap-1.5 text-base text-primary-foreground/60">
            <Link to="/" className="transition-colors hover:text-primary-foreground">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/services" className="transition-colors hover:text-primary-foreground">Services</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-primary-foreground">For Individuals</span>
          </nav>
          <motion.h1 {...fadeUp()} className="mx-auto max-w-4xl text-3xl font-heading font-bold text-primary-foreground md:text-5xl lg:text-6xl">
            {getString(content.heroTitle, "Individual Coaching & Personal Branding: Cultivate Clarity Within.")}
          </motion.h1>
          <motion.p {...fadeUp(0.1)} className="mx-auto mt-3 max-w-2xl font-sans text-lg text-primary-foreground/80">
            {getString(content.heroDescription, "Personalized coaching for career clarity, confidence, and personal transformation.")}
          </motion.p>
        </div>
      </section>

      <section className="bg-background py-12">
        <div className="container mx-auto px-4">
          <motion.h2 {...fadeUp()} className="text-center text-3xl font-heading font-bold text-foreground md:text-5xl">{getString(content.problemsTitle, "Challenges You May Be Facing")}</motion.h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {problems.map((problem, i) => {
              const Icon = problemIcons[i] ?? Compass;
              return (
                <motion.div key={problem.title} {...fadeUp(i * 0.08)} className="rounded-xl bg-card p-5 shadow-sm transition-shadow hover:shadow-md">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-accent/15">
                    <Icon className="h-5 w-5 text-accent" />
                  </div>
                  <h3 className="mt-3 text-lg font-heading font-bold text-foreground">{problem.title}</h3>
                  <p className="mt-1.5 font-sans text-base leading-relaxed text-muted-foreground">{problem.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Life Coaching */}
      <section id="life-coaching" className="bg-muted py-12">
        <div className="container mx-auto grid items-center gap-10 px-4 md:grid-cols-2">
          <motion.img
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600"
            alt="Life Coaching session"
            className="h-64 w-full rounded-2xl object-cover shadow-lg md:h-80"
            loading="lazy"
          />
          <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-2xl font-heading font-bold text-foreground md:text-4xl">{getString(content.coachingTitle, "Life Coaching for Working Professionals")}</h2>
            <p className="mt-1.5 text-base font-semibold uppercase tracking-wide text-primary">{getString(content.coachingAudience, "Individual contributors, managers, entrepreneurs, professionals")}</p>
            <ul className="mt-4 space-y-2.5">
              {coachingFocus.map((item, index) => {
                const Icon = coachingIcons[index] ?? Target;
                return (
                  <li key={item} className="flex items-center gap-2.5">
                    <Icon className="h-5 w-5 shrink-0 text-accent" />
                    <span className="font-sans text-base text-muted-foreground">{item}</span>
                  </li>
                );
              })}
            </ul>
            <div className="mt-4 border-l-4 border-accent py-1.5 pl-3">
              <p className="font-sans text-base italic leading-relaxed text-muted-foreground">{getString(content.coachingQuote, "Guided by 'Clarity Within. Success Beyond.', Mahirah helps working professionals move from overwhelm to clarity, enabling sustainable performance and personal alignment.")}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Personal Branding */}
      <section id="personal-branding" className="bg-background py-12">
        <div className="container mx-auto grid items-center gap-10 px-4 md:grid-cols-2">
          <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-2xl font-heading font-bold text-foreground md:text-4xl">{getString(content.brandingTitle, "Personal Branding through Image Consulting")}</h2>
            <p className="mt-3 font-sans text-base leading-relaxed text-muted-foreground">{getString(content.brandingDescription, "Discover your authentic self and cultivate unshakeable confidence through personalized style coaching, strategic use of colors, and effective body language.")}</p>
            <ul className="mt-4 space-y-2.5">
              {brandingIncludes.map((item, index) => {
                const Icon = brandingIcons[index] ?? Shirt;
                return (
                  <li key={item} className="flex items-center gap-2.5">
                    <Icon className="h-5 w-5 shrink-0 text-accent" />
                    <span className="font-sans text-base text-muted-foreground">{item}</span>
                  </li>
                );
              })}
            </ul>
          </motion.div>
          <motion.img
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            src="https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=600"
            alt="Personal branding and image consulting"
            className="h-64 w-full rounded-2xl object-cover shadow-lg md:h-80"
            loading="lazy"
          />
        </div>
      </section>

      {/* Emotional Intelligence */}
      <section id="emotional-intelligence" className="bg-muted py-12">
        <div className="container mx-auto grid items-center gap-10 px-4 md:grid-cols-2">
          <motion.img
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=600"
            alt="Emotional Intelligence coaching"
            className="h-64 w-full rounded-2xl object-cover shadow-lg md:h-80"
            loading="lazy"
          />
          <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-2xl font-heading font-bold text-foreground md:text-4xl">Emotional Intelligence & Self-Awareness</h2>
            <p className="mt-1.5 text-base font-semibold uppercase tracking-wide text-primary">For professionals seeking deeper self-understanding</p>
            <p className="mt-3 font-sans text-base leading-relaxed text-muted-foreground">
              Develop the ability to recognize, understand, and manage your emotions — and those of others. Mahirah's EI coaching helps you navigate workplace dynamics, build stronger relationships, and lead with empathy and composure.
            </p>
            <ul className="mt-4 space-y-2.5">
              {["Self-awareness & emotional regulation", "Empathy and active listening", "Managing difficult conversations", "Building trust and rapport"].map((item) => (
                <li key={item} className="flex items-center gap-2.5">
                  <Brain className="h-5 w-5 shrink-0 text-accent" />
                  <span className="font-sans text-base text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Graphology */}
      <section id="graphology" className="bg-background py-12">
        <div className="container mx-auto grid items-center gap-10 px-4 md:grid-cols-2">
          <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-2xl font-heading font-bold text-foreground md:text-4xl">Graphology (Handwriting Analysis)</h2>
            <p className="mt-3 font-sans text-base leading-relaxed text-muted-foreground">
              Unlock personality insights through the science of handwriting analysis. Graphology reveals subconscious patterns, strengths, and areas for growth — offering a unique lens into self-discovery and personal development.
            </p>
            <ul className="mt-4 space-y-2.5">
              {["Personality trait identification", "Behavioural pattern awareness", "Career aptitude insights", "Therapeutic handwriting exercises"].map((item) => (
                <li key={item} className="flex items-center gap-2.5">
                  <PenTool className="h-5 w-5 shrink-0 text-accent" />
                  <span className="font-sans text-base text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.img
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            src="https://images.unsplash.com/photo-1455390582262-044cdead277a?w=600"
            alt="Graphology and handwriting analysis"
            className="h-64 w-full rounded-2xl object-cover shadow-lg md:h-80"
            loading="lazy"
          />
        </div>
      </section>

      {/* Art Therapy */}
      <section id="art-therapy" className="bg-muted py-12">
        <div className="container mx-auto grid items-center gap-10 px-4 md:grid-cols-2">
          <motion.img
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            src="https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=600"
            alt="Art Therapy session"
            className="h-64 w-full rounded-2xl object-cover shadow-lg md:h-80"
            loading="lazy"
          />
          <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-2xl font-heading font-bold text-foreground md:text-4xl">Art Therapy for Stress Relief & Confidence</h2>
            <p className="mt-1.5 text-base font-semibold uppercase tracking-wide text-primary">Creative healing for inner calm and self-expression</p>
            <p className="mt-3 font-sans text-base leading-relaxed text-muted-foreground">
              Art Therapy uses creative expression as a powerful tool for emotional healing, stress reduction, and building self-confidence. No artistic skill is required — the process itself is the therapy.
            </p>
            <ul className="mt-4 space-y-2.5">
              {["Stress and anxiety management", "Emotional expression & processing", "Confidence building through creativity", "Mindfulness and relaxation techniques"].map((item) => (
                <li key={item} className="flex items-center gap-2.5">
                  <Flower2 className="h-5 w-5 shrink-0 text-accent" />
                  <span className="font-sans text-base text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Journey */}
      <section className="bg-background py-12">
        <div className="container mx-auto px-4 text-center">
          <motion.h2 {...fadeUp()} className="text-3xl font-heading font-bold text-foreground md:text-5xl">{getString(content.journeyTitle, "Your Journey With Us")}</motion.h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {journey.map((item, i) => {
              const Icon = journeyIcons[i] ?? Phone;
              return (
                <motion.div key={item.title} {...fadeUp(i * 0.12)} className="relative rounded-xl bg-card p-6 text-center shadow-sm">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-xl font-bold text-accent-foreground">{i + 1}</div>
                  <Icon className="mx-auto mt-2.5 h-5 w-5 text-accent" />
                  <h3 className="mt-2.5 text-lg font-heading font-bold text-foreground">{item.title}</h3>
                  <p className="mt-1.5 font-sans text-base text-muted-foreground">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-accent py-12">
        <div className="container mx-auto px-4 text-center">
          <motion.h2 {...fadeUp()} className="text-3xl font-heading font-bold text-accent-foreground md:text-5xl">{getString(content.ctaTitle, "Book Your Complimentary Discovery Call")}</motion.h2>
          <motion.div {...fadeUp(0.15)} className="mt-6">
            <DiscoveryCallDialog>
              <Button size="lg" className="bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
                {getString(content.ctaButtonLabel, "Get Started →")}
              </Button>
            </DiscoveryCallDialog>
          </motion.div>
        </div>
      </section>

      <section className="bg-background py-10">
        <div className="container mx-auto px-4">
          <AuthorByline />
        </div>
      </section>

      <RelatedServices current="individuals" />
    </div>
  );
};

export default ServicesIndividuals;
